import { Close } from '@mui/icons-material';
import { styled } from '@mui/material';
import { format } from 'date-fns';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import ArrowDownSvgIcon from '../../components/SvgIcon/ArrowDownSvgIcon';
import ArrowUpSvgIcon from '../../components/SvgIcon/ArrowUpSvgIcon';
import ReportSvgIcon from '../../components/SvgIcon/ReportSvgIcon';

import { Task, TaskGroup } from '../../apis/tasksApi';
import { getDasEndpoints } from '../../apis/dasEndpointApi';
import IconContainer from '../../components/IconContainer';
import { DasEndpoint } from '../../types/Device';
import { Project, ProjectMap } from '../../types/Project';
import { getFinishDate, getTaskGroupType } from '../../utils/constant';
import TaskStatusGroupIcon from './TaskStatusGroupIcon';
import TaskStatusIcon from './TaskStatusIcon';
import FinalDateGroupDisplay from './FinalDateGroupDisplay';
import FinalDateTaskDisplay from './FinalDateTaskDisplay';
import { PanelInformationDetail } from './panel-information/PanelInformationDetail';

const Container = styled('div')`
  & > .item {
    min-width: 1100px;
    display: grid;
    align-items: center;
    grid-template-columns: 40px 1fr 1fr 1fr 80px 150px 150px 80px 32px;

    min-height: 72px;
    font-size: 1rem;
    line-height: 1.5;
    color: ${({ theme }) => theme.color.secondary.$100};
    background-color: #fff;
    box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.1);
    padding: 11px 24px;
    margin: 8px 0;

    & > div {
      display: flex;
      &:first-of-type {
        cursor: pointer;
      }

      &:nth-of-type(2) {
        justify-content: start;
      }

      &:nth-of-type(3) {
        color: ${({ theme }) => theme.color.secondary.$80};
      }

      &:nth-of-type(4) {
        color: ${({ theme }) => theme.color.primary.$100};
      }
      &:nth-of-type(5) {
        color: ${({ theme }) => theme.color.primary.$100};
      }

      &.timestamp {
        display: flex;
        flex-direction: row;
        font-family: 'Noto Sans';
        font-size: 16px;
        font-style: normal;

        & span {
          font-size: 14px;
          font-weight: 400;
          line-height: 24px;
          &:first-of-type {
            margin-right: 5px;
          }
        }
      }
    }
  }
  & > .info {
    background-color: #e5e5e5;
    display: grid;
    grid-template-columns: 40px 1fr 1fr 1fr 80px 150px 150px 80px 32px;

    padding: 11px 24px;
    align-items: center;
    & > div {
      display: flex;
      &.timestamp {
        display: flex;
        flex-direction: row;
        font-family: 'Noto Sans';
        font-size: 16px;
        font-style: normal;
        & span {
          font-size: 14px;
          font-weight: 400;
          line-height: 24px;
          &:first-of-type {
            margin-right: 5px;
          }
        }
      }
    }
  }
`;

interface TaskItemProps {
  taskGroup: TaskGroup;
  projectMap: ProjectMap | undefined;
}

const TaskItem: React.FC<TaskItemProps> = ({ taskGroup, projectMap }) => {
  const [isExpand, setIsExpand] = useState(false);
  const [isOpenPanel, setIsOpenPanel] = useState(false);
  const [taskSelected, setTaskSelected] = useState<Task>();

  // Add query for device data
  const { data: deviceData } = useQuery({
    queryKey: ['get-devices', taskGroup.id, isExpand],
    queryFn: async () => {
      if (!isExpand) return {};

      const deviceIds = taskGroup.tasks
        .map((task) => task.endpointId)
        .filter(Boolean);
      const responses = await Promise.all(
        deviceIds.map((id) => getDasEndpoints({ id })),
      );

      return responses.reduce(
        (acc, response) => {
          const device = response.data.data[0];
          if (device) {
            acc[device.id] = device;
          }
          return acc;
        },
        {} as Record<string, DasEndpoint>,
      );
    },
    enabled: isExpand,
  });

  const createDate = new Date(taskGroup.createdAt);

  const finishDate = getFinishDate(taskGroup);

  const projectNames: string[] | null = taskGroup?.tasks
    ?.map((task) => {
      if (task && projectMap) {
        return (projectMap?.[task.projectId] as Project)?.name;
      }
      return undefined;
    })
    .filter((name): name is string => name !== undefined);

  return (
    <Container>
      <div className="item">
        <div onClick={() => setIsExpand((prev) => !prev)}>
          {isExpand ? (
            <ArrowUpSvgIcon sx={{ width: '40px', height: '40px' }} />
          ) : (
            <ArrowDownSvgIcon sx={{ width: '40px', height: '40px' }} />
          )}
        </div>
        <div>{getTaskGroupType(taskGroup.type)}</div>
        <div>{taskGroup.name}</div>
        <div>{projectNames.length > 0 ? projectNames[0] : 'N/A'}</div>

        <div>{taskGroup.tasks.length}</div>
        <div className="timestamp">
          <span>{format(createDate, 'yyyy-MM-dd')}</span>
          <span>{format(createDate, 'hh:mm:ss')}</span>
        </div>
        <div className="timestamp">
          <FinalDateGroupDisplay
            status={taskGroup.status}
            finishDate={finishDate}
          />
        </div>
        <TaskStatusGroupIcon status={taskGroup.status} />

        <div>
          {taskGroup.status == 'inProgress' && (
            <IconContainer>
              <Close sx={{ width: '14px', height: '14px' }} />
            </IconContainer>
          )}
        </div>
      </div>
      {isExpand &&
        taskGroup.tasks.map((taskChild, index) => {
          const device = deviceData?.[taskChild?.endpointId ?? ''];

          const status = taskChild.status;
          const createdAt = taskChild?.createdAt;

          const finishAt = taskChild.updatedAt
            ? new Date(taskChild.createdAt)
            : null;
          const taskChildName = taskChild.payload?.action?.type;
          return (
            <div
              key={`task-child-${taskChild.endpointId}-index-${index}`}
              className="info"
            >
              <div>ID</div>
              <div>{device?.dasId ? device?.dasId : 'loading'}</div>
              <div>{taskChildName ?? null}</div>
              <div />
              <div />
              <div className="timestamp">
                {' '}
                <>
                  <span>{format(createdAt, 'yyyy-MM-dd')}</span>
                  <span>{format(createdAt, 'hh:mm:ss')}</span>
                </>
              </div>
              <div className="timestamp">
                <FinalDateTaskDisplay
                  finishDate={finishAt}
                  status={taskChild.status}
                />
              </div>

              <TaskStatusIcon status={status} />
              <IconContainer
                disabled={taskChild.status == 'inProgress'}
                onClick={() => {
                  setTaskSelected(taskChild);
                  setIsOpenPanel(true);
                }}
              >
                <ReportSvgIcon />
              </IconContainer>
            </div>
          );
        })}
      <PanelInformationDetail
        open={isOpenPanel}
        onClose={() => {
          setIsOpenPanel(false);
        }}
        task={taskSelected}
        taskGroup={taskGroup}
        deviceMap={deviceData ?? {}}
      />
    </Container>
  );
};

export default TaskItem;
